import React from 'react';
import styles from './RoutesTab.module.css';
import { RouteDetail } from '../routeDensityTypes';

interface RoutesTabProps {
  routes: RouteDetail[];
}

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'critical') {
    return (
      <div className={styles.statusIconCritical}>
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
        </svg>
      </div>
    );
  }
  return (
    <div className={styles.statusIconHealthy}>
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
    </div>
  );
};

export const RoutesTab: React.FC<RoutesTabProps> = ({ routes }) => {
  return (
    <div className={styles.list}>
      {routes.map((route) => {
        const isCritical = route.status === 'critical';
        const hasWarnings = route.warnings && route.warnings.length > 0;
        const hasIssues = route.issues && route.issues.length > 0;

        return (
          <div
            key={route.id}
            className={`${styles.card} ${isCritical ? styles.cardCritical : ''}`}
          >
            {/* Card Header */}
            <div className={styles.cardHeader}>
              <div className={styles.headerLeft}>
                <StatusIcon status={route.status} />
                <div>
                  <p className={styles.routeName}>{route.name}</p>
                  <p className={styles.routeMeta}>{route.zipCodes}</p>
                  <p className={styles.routeMeta}>{route.activeDays}</p>
                </div>
              </div>
              <div className={styles.headerRight}>
                <p className={styles.revenue}>{route.revenue}</p>
                <p className={`${styles.density} ${isCritical ? styles.densityCritical : styles.densityGood}`}>
                  Density: {route.density}
                </p>
              </div>
            </div>

            {/* Stats Row */}
            <div className={styles.statsRow}>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Stops</span>
                <span className={styles.statValue}>{route.stops}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>$/Stop</span>
                <span className={styles.statValue}>{route.dollarPerStop}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Miles</span>
                <span className={styles.statValue}>{route.miles}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Time</span>
                <span className={styles.statValue}>{route.time}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Cost/1k</span>
                <span className={`${styles.statValue} ${isCritical ? styles.costCritical : styles.costNormal}`}>
                  {route.costPer1K}
                </span>
              </div>
            </div>

            {/* Warnings */}
            {hasWarnings && (
              <div className={styles.warningBanner}>
                <span className={styles.warningIcon}>⚠️</span>
                <div>
                  <p className={styles.warningTitle}>Warning:</p>
                  {route.warnings!.map((w, i) => (
                    <p key={i} className={styles.warningText}>· {w}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Critical Issues */}
            {hasIssues && (
              <div className={styles.issuesBanner}>
                <span className={styles.issuesIcon}>🚨</span>
                <div>
                  <p className={styles.issuesTitle}>Issues Detected:</p>
                  {route.issues!.map((issue, i) => (
                    <p key={i} className={styles.issueText}>· {issue}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
